import { ui } from '@libs/domain';
import { type FieldErrorField, useItemNames } from '@ps-smart-cp/ppfw-client';
import { type Ref, forwardRef, useImperativeHandle, useRef } from 'react';
import { SegmentMstRangeSearch, type SegmentMstRangeSearchRef, SegmentMstSearchField } from './components';
import { useSegmentMstSettings } from './hooks';
import type { SingleSegmentMst, SingleSegmentMstRange } from './models';
import { createSegmentMstCodeItem, createSegmentMstCodeRangeItem } from './services';

interface SegmentMstsSearchProps {
  segmentMsts: SingleSegmentMst[];
  setSegmentMsts: (segmentMsts: SingleSegmentMst[]) => void;
  errorFields?: string[];
}

function SegmentMstsSearch(props: SegmentMstsSearchProps) {
  const { segmentMsts, setSegmentMsts, errorFields } = props;
  const segmentMstSettings = useSegmentMstSettings();

  return segmentMstSettings.map((s) => (
    <SegmentMstSearchField
      key={s.key}
      index={s.key}
      segmentMstKey={s.key}
      obj={segmentMsts.find((f) => f.key === s.key)!}
      setObj={(newSegmentMsts) =>
        setSegmentMsts(
          segmentMsts.map((f) =>
            f.key === s.key ? { key: f.key, segmentMstCode: newSegmentMsts.segmentMstCode } : f,
          ),
        )
      }
      errorFields={errorFields}
      prop="segmentMstCode"
      items={createSegmentMstCodeItem(s.key, segmentMstSettings, true)}
    />
  ));
}

interface SegmentMstsRangeSearchProps {
  segmentMstsRange: SingleSegmentMstRange[];
  setSegmentMstsRange: (segmentMsts: SingleSegmentMstRange[]) => void;
  errorFields?: string[];
}

interface SegmentMstsRangeSearchRef {
  validate: () => FieldErrorField[];
}

function SegmentMstsRangeSearchInnerComponent(
  props: SegmentMstsRangeSearchProps,
  ref: Ref<SegmentMstsRangeSearchRef>,
) {
  const { segmentMstsRange, setSegmentMstsRange, errorFields } = props;
  const segmentMstSettings = useSegmentMstSettings();
  const getUiName = useItemNames(ui.labelNames);
  const segmentMstRangeSearchRefs = useRef<SegmentMstRangeSearchRef[]>([]);
  const validate = () => {
    return segmentMstSettings
      .map((_, i) => segmentMstRangeSearchRefs.current[i].validate())
      .reduce((prevValus, currValues) => prevValus.concat(currValues));
  };
  useImperativeHandle(ref, () => ({ validate }));

  return segmentMstSettings.map((s, i) => (
    <SegmentMstRangeSearch
      ref={(el) => (segmentMstRangeSearchRefs.current[i] = el!)}
      key={s.key}
      index={i}
      segmentMstKey={s.key}
      obj={segmentMstsRange.find((f) => f.key === s.key)!}
      setObj={(newSegmentMstsRange) =>
        setSegmentMstsRange(
          segmentMstsRange.map((f) =>
            f.key === s.key
              ? { key: f.key, segmentMstCodeRange: newSegmentMstsRange.segmentMstCodeRange }
              : f,
          ),
        )
      }
      errorFields={errorFields}
      prop="segmentMstCodeRange"
      items={createSegmentMstCodeRangeItem(s.key, segmentMstSettings, getUiName('range'))}
    />
  ));
}

const SegmentMstsRangeSearch = forwardRef(SegmentMstsRangeSearchInnerComponent);
export { SegmentMstsRangeSearch, SegmentMstsSearch };
export type { SegmentMstsRangeSearchProps, SegmentMstsRangeSearchRef, SegmentMstsSearchProps };
